import { useEffect, useMemo, useState } from "react";
import {
  BriefcaseIcon,
  CheckCircle2,
  MapPin,
  Pencil,
  Plus,
  RefreshCw,
  Search,
  Trash2,
  X,
} from "lucide-react";

import { useAuth } from "#/context/AuthProvider";
import { leaveApi } from "../api";
import { useConfirm } from "#/components/common/ConfirmProvider";
import { LeaveBackButton } from "../components/LeaveBackButton";
import { LeaveError } from "../components/LeaveError";
import { LeaveLoading } from "../components/LeaveLoading";
import { LeavePageHeader } from "../components/LeavePageHeader";
import { LeavePagination } from "../components/LeavePagination";
import {
  Btn,
  EmptyState,
  Table,
  TBody,
  TD,
  TH,
  THead,
  TR,
} from "../components/LeaveUI";
import { useAsyncData, useMutation, usePagination } from "../hooks";
import { MODULE_LABELS, MODULE_ORDER } from "../types";
import type {
  ApprovalModule,
  DelegateCandidateOption,
  OfficialDutyCreateRequest,
  OfficialDutyResponse,
} from "../types";

// ─── Main page ───────────────────────────────────────────────────────────────

export default function OfficialDutyPage() {
  const { hasPermission, hasRole } = useAuth();
  const canManage = hasRole("SUPER_ADMIN") || hasPermission("HR_LEAVE_MANAGE");

  const pagination = usePagination();
  const [filterStart, setFilterStart] = useState("");
  const [filterEnd, setFilterEnd] = useState("");

  const { data, loading, error, reload } = useAsyncData(
    () =>
      leaveApi.officialDutyList(
        pagination.page,
        pagination.size,
        filterStart || undefined,
        filterEnd || undefined,
      ),
    [pagination.page, pagination.size, filterStart, filterEnd],
  );

  const [editing, setEditing] = useState<OfficialDutyResponse | null>(null);
  const [showForm, setShowForm] = useState(false);

  function openCreate() {
    setEditing(null);
    setShowForm(true);
  }

  function openEdit(record: OfficialDutyResponse) {
    setEditing(record);
    setShowForm(true);
  }

  function closeForm() {
    setShowForm(false);
    setEditing(null);
  }

  async function handleSaved() {
    closeForm();
    await reload();
  }

  return (
    <div className="space-y-6">
      <LeavePageHeader
        breadcrumb="Leave Management"
        title="Official Duty Records"
        actions={
          <div className="flex items-center gap-2">
            <LeaveBackButton to="/hr/leave" label="Dashboard" />
            <Btn variant="secondary" size="md" icon={RefreshCw} onClick={reload}>
              Refresh
            </Btn>
            {canManage && (
              <Btn variant="primary" size="md" icon={Plus} onClick={openCreate}>
                Add Record
              </Btn>
            )}
          </div>
        }
      />

      {/* ── Filters ── */}
      <div className="flex flex-wrap items-end gap-3 rounded-2xl border bg-white px-5 py-4 shadow-sm">
        <label className="space-y-1">
          <span className="text-xs font-semibold text-slate-500">From</span>
          <input
            type="date"
            className="input"
            value={filterStart}
            onChange={(e) => { setFilterStart(e.target.value); pagination.reset(); }}
          />
        </label>
        <label className="space-y-1">
          <span className="text-xs font-semibold text-slate-500">To</span>
          <input
            type="date"
            className="input"
            value={filterEnd}
            onChange={(e) => { setFilterEnd(e.target.value); pagination.reset(); }}
          />
        </label>
        {(filterStart || filterEnd) && (
          <button
            type="button"
            onClick={() => { setFilterStart(""); setFilterEnd(""); pagination.reset(); }}
            className="flex items-center gap-1 rounded-xl border px-3 py-2 text-xs font-semibold text-slate-600 hover:bg-slate-50"
          >
            <X className="h-3 w-3" /> Clear
          </button>
        )}
      </div>

      {/* ── Inline add/edit form ── */}
      {showForm && (
        <OfficialDutyForm
          initial={editing}
          onSaved={handleSaved}
          onCancel={closeForm}
        />
      )}

      {loading && <LeaveLoading label="Loading official duty records…" />}
      {error   && <LeaveError message={error} onRetry={reload} />}

      {!loading && data && (
        <>
          <Table>
            <THead>
              <TH>Employee</TH>
              <TH>Period</TH>
              <TH>Purpose</TH>
              <TH>Location</TH>
              <TH>Recorded By</TH>
              {canManage && <TH right>Actions</TH>}
            </THead>
            <TBody>
              {data.content.length === 0 ? (
                <tr>
                  <td colSpan={canManage ? 6 : 5}>
                    <EmptyState
                      icon={BriefcaseIcon}
                      title="No official duty records"
                      description="Records added here will also appear in the Relief Employee availability check."
                    />
                  </td>
                </tr>
              ) : (
                data.content.map((record) => (
                  <OfficialDutyRow
                    key={record.id}
                    record={record}
                    canManage={canManage}
                    onEdit={() => openEdit(record)}
                    onDeleted={reload}
                  />
                ))
              )}
            </TBody>
          </Table>

          <LeavePagination
            page={data}
            onPageChange={pagination.setPage}
            onSizeChange={pagination.setSize}
          />
        </>
      )}
    </div>
  );
}

// ─── Row ─────────────────────────────────────────────────────────────────────

function OfficialDutyRow({
  record,
  canManage,
  onEdit,
  onDeleted,
}: {
  record: OfficialDutyResponse;
  canManage: boolean;
  onEdit: () => void;
  onDeleted: () => void;
}) {
  const confirm = useConfirm();
  const del = useMutation(() => leaveApi.deleteOfficialDuty(record.id));

  async function handleDelete() {
    if (!(await confirm(`Remove official duty record for ${record.employeeName ?? "this employee"}?`))) return;
    await del.mutate(undefined as never);
    onDeleted();
  }

  return (
    <TR>
      <TD>
        <div className="flex items-center gap-2.5">
          <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-purple-100 text-xs font-black text-purple-700">
            {(record.employeeName ?? "?").charAt(0).toUpperCase()}
          </div>
          <div>
            <div className="font-semibold text-slate-800">{record.employeeName ?? "—"}</div>
            <div className="text-xs text-slate-400">
              {[record.employeeNumber, record.departmentName].filter(Boolean).join(" · ")}
            </div>
          </div>
        </div>
      </TD>
      <TD className="whitespace-nowrap text-slate-600">
        {fmtDate(record.startDate)} → {fmtDate(record.endDate)}
      </TD>
      <TD className="text-slate-700">{record.purpose || <span className="text-slate-300">—</span>}</TD>
      <TD>
        {record.location ? (
          <span className="inline-flex items-center gap-1 text-sm text-slate-600">
            <MapPin className="h-3.5 w-3.5 text-slate-400" />
            {record.location}
          </span>
        ) : (
          <span className="text-slate-300">—</span>
        )}
      </TD>
      <TD muted className="text-xs">{record.recordedByName || "—"}</TD>
      {canManage && (
        <TD right>
          <div className="flex items-center justify-end gap-1">
            <button
              type="button"
              onClick={onEdit}
              className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-700"
              title="Edit"
            >
              <Pencil className="h-3.5 w-3.5" />
            </button>
            <button
              type="button"
              onClick={handleDelete}
              disabled={del.loading}
              className="rounded-lg p-1.5 text-slate-400 hover:bg-red-50 hover:text-red-600 disabled:opacity-40"
              title="Delete"
            >
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          </div>
          {del.error && (
            <p className="mt-1 text-right text-xs text-red-600">{del.error}</p>
          )}
        </TD>
      )}
    </TR>
  );
}

// ─── Add / Edit form ─────────────────────────────────────────────────────────

type FormState = {
  employeeId: string;
  startDate: string;
  endDate: string;
  purpose: string;
  location: string;
  notes: string;
  delegateUserId: string;
  delegateModules: ApprovalModule[];
};

function OfficialDutyForm({
  initial,
  onSaved,
  onCancel,
}: {
  initial: OfficialDutyResponse | null;
  onSaved: () => void;
  onCancel: () => void;
}) {
  const [form, setForm] = useState<FormState>({
    employeeId: initial?.employeeId?.toString() ?? "",
    startDate:  initial?.startDate ?? "",
    endDate:    initial?.endDate ?? "",
    purpose:    initial?.purpose ?? "",
    location:   initial?.location ?? "",
    notes:      initial?.notes ?? "",
    delegateUserId: "",
    delegateModules: [],
  });
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  // Employee picker — re-use the employee options endpoint
  const employees = useAsyncData(() => leaveApi.employeeOptions(), []);

  // ── optional approval delegation (new records only) ──
  const [delegateCandidates, setDelegateCandidates] = useState<DelegateCandidateOption[]>([]);
  const [delegateQuery, setDelegateQuery] = useState("");
  const [delegateOpen, setDelegateOpen] = useState(false);
  // Modules the EMPLOYEE GOING ON DUTY actually holds approval rights in -- not the HR
  // admin's own modules -- so the checklist only ever offers what that person could
  // otherwise approve themselves.
  const [availableModules, setAvailableModules] = useState<ApprovalModule[]>([]);

  useEffect(() => {
    if (initial) return; // editing an existing record — delegation isn't editable here
    let active = true;
    leaveApi.delegationCandidates()
      .then((candidates) => { if (active) setDelegateCandidates(candidates); })
      .catch(() => { if (active) setDelegateCandidates([]); });
    return () => { active = false; };
  }, [initial]);

  useEffect(() => {
    if (initial) return; // editing an existing record — delegation isn't editable here
    const employeeId = Number(form.employeeId);
    if (!form.employeeId || Number.isNaN(employeeId)) {
      setAvailableModules([]);
      return;
    }
    let active = true;
    leaveApi.modulesForEmployee(employeeId)
      .then((modules) => { if (active) setAvailableModules(modules); })
      .catch(() => { if (active) setAvailableModules([]); });
    return () => { active = false; };
  }, [initial, form.employeeId]);

  const selectedDelegate = useMemo(
    () => delegateCandidates.find((e) => e.userAccountId === Number(form.delegateUserId)) ?? null,
    [delegateCandidates, form.delegateUserId],
  );
  const filteredDelegates = useMemo(() => {
    const q = delegateQuery.trim().toLowerCase();
    const candidates = delegateCandidates.filter((e) => e.userAccountId.toString() !== form.employeeId);
    if (!q) return candidates;
    return candidates.filter((e) =>
      [e.fullName, e.employeeNumber, e.departmentName, e.jobTitle]
        .filter(Boolean)
        .join(" ")
        .toLowerCase()
        .includes(q),
    );
  }, [delegateCandidates, delegateQuery, form.employeeId]);

  function toggleDelegateModule(module: ApprovalModule) {
    setForm((p) => ({
      ...p,
      delegateModules: p.delegateModules.includes(module)
        ? p.delegateModules.filter((m) => m !== module)
        : [...p.delegateModules, module],
    }));
  }

  // Backdated official duty (recorded after the fact) cannot carry a delegation -- same
  // rule as the leave apply form: only a current or future-dated record supports it.
  const isBackdated = useMemo(
    () => !!form.startDate && form.startDate < new Date().toISOString().slice(0, 10),
    [form.startDate],
  );

  useEffect(() => {
    if (!isBackdated) return;
    setForm((p) => (p.delegateUserId || p.delegateModules.length
      ? { ...p, delegateUserId: "", delegateModules: [] }
      : p));
  }, [isBackdated]);

  function set(k: keyof FormState, v: string) {
    setForm((p) => ({ ...p, [k]: v }));
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!form.employeeId || !form.startDate || !form.endDate) return;
    if (!initial && form.delegateUserId && form.delegateModules.length === 0) {
      setSubmitError("Select at least one module to delegate, or clear the delegate you selected.");
      return;
    }
    const payload: OfficialDutyCreateRequest = {
      employeeId: Number(form.employeeId),
      startDate:  form.startDate,
      endDate:    form.endDate,
      purpose:    form.purpose.trim() || null,
      location:   form.location.trim() || null,
      notes:      form.notes.trim() || null,
      ...(!initial && !isBackdated && form.delegateUserId
        ? { delegateUserId: Number(form.delegateUserId), delegateModules: form.delegateModules }
        : {}),
    };
    setSaving(true);
    setSubmitError(null);
    try {
      if (initial) {
        await leaveApi.updateOfficialDuty(initial.id, payload);
      } else {
        await leaveApi.createOfficialDuty(payload);
      }
      onSaved();
    } catch (err: unknown) {
      setSubmitError(err instanceof Error ? err.message : "Failed to save record.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="overflow-hidden rounded-2xl border border-purple-200 bg-white shadow-sm">
      <div className="flex items-center justify-between border-b border-purple-100 bg-purple-50 px-5 py-4">
        <div className="flex items-center gap-2">
          <BriefcaseIcon className="h-4 w-4 text-purple-600" />
          <h2 className="font-semibold text-purple-900">
            {initial ? "Edit Official Duty Record" : "Add Official Duty Record"}
          </h2>
        </div>
        <button
          type="button"
          onClick={onCancel}
          className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-100"
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4 p-5">
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">

          {/* Employee */}
          <label className="space-y-1.5 lg:col-span-1">
            <span className="text-sm font-semibold text-slate-700">
              Employee <span className="text-red-500">*</span>
            </span>
            {employees.loading ? (
              <div className="input text-slate-400">Loading employees…</div>
            ) : (
              <select
                className="input"
                value={form.employeeId}
                onChange={(e) => set("employeeId", e.target.value)}
                required
              >
                <option value="">Select employee…</option>
                {(employees.data ?? []).map((e) => (
                  <option key={e.id} value={e.id}>
                    {e.employeeName ?? e.fullName ?? e.name ?? `Employee #${e.id}`}
                    {e.employeeNumber ? ` (${e.employeeNumber})` : ""}
                    {e.departmentName ? ` · ${e.departmentName}` : ""}
                  </option>
                ))}
              </select>
            )}
          </label>

          {/* Start Date */}
          <label className="space-y-1.5">
            <span className="text-sm font-semibold text-slate-700">
              Start Date <span className="text-red-500">*</span>
            </span>
            <input
              type="date"
              className="input"
              value={form.startDate}
              onChange={(e) => { set("startDate", e.target.value); if (form.endDate && e.target.value > form.endDate) set("endDate", ""); }}
              required
            />
          </label>

          {/* End Date */}
          <label className="space-y-1.5">
            <span className="text-sm font-semibold text-slate-700">
              End Date <span className="text-red-500">*</span>
            </span>
            <input
              type="date"
              className="input"
              value={form.endDate}
              min={form.startDate || undefined}
              onChange={(e) => set("endDate", e.target.value)}
              required
            />
          </label>

          {/* Purpose */}
          <label className="space-y-1.5">
            <span className="text-sm font-semibold text-slate-700">Purpose</span>
            <input
              type="text"
              className="input"
              value={form.purpose}
              onChange={(e) => set("purpose", e.target.value)}
              placeholder="e.g. Conference, Field inspection…"
            />
          </label>

          {/* Location */}
          <label className="space-y-1.5">
            <span className="text-sm font-semibold text-slate-700">Location</span>
            <input
              type="text"
              className="input"
              value={form.location}
              onChange={(e) => set("location", e.target.value)}
              placeholder="e.g. Nairobi, Site A…"
            />
          </label>
        </div>

        {/* Notes */}
        <label className="block space-y-1.5">
          <span className="text-sm font-semibold text-slate-700">Notes</span>
          <textarea
            className="input min-h-[72px] resize-y"
            value={form.notes}
            onChange={(e) => set("notes", e.target.value)}
            placeholder="Any additional notes…"
          />
        </label>

        {/* Optional approval delegation — new records only */}
        {initial ? (
          <p className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-xs text-slate-500">
            Approval delegation can only be set up when a record is first created.
          </p>
        ) : isBackdated ? (
          <p className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-xs text-amber-700">
            Not available for a backdated record -- approval delegation can only be set up
            for official duty starting today or later.
          </p>
        ) : (
          <div className="rounded-xl border border-dashed border-slate-300 p-4">
            <p className="mb-2 text-xs font-bold uppercase tracking-widest text-slate-400">
              Delegate Approval Rights <span className="font-normal text-slate-400">(optional)</span>
            </p>

            <div className="relative">
              <div className="relative">
                <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400" />
                <input
                  type="text"
                  className="input pl-8 pr-7"
                  placeholder="Search by name, number, or department…"
                  value={selectedDelegate ? selectedDelegate.fullName : delegateQuery}
                  onChange={(e) => {
                    if (form.delegateUserId) set("delegateUserId", "");
                    setDelegateQuery(e.target.value);
                    setDelegateOpen(true);
                  }}
                  onFocus={() => setDelegateOpen(true)}
                  onBlur={() => setTimeout(() => setDelegateOpen(false), 150)}
                />
                {(selectedDelegate || delegateQuery) && (
                  <button
                    type="button"
                    onMouseDown={() => {
                      set("delegateUserId", "");
                      setForm((p) => ({ ...p, delegateModules: [] }));
                      setDelegateQuery("");
                    }}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-300 hover:text-slate-500"
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                )}
              </div>

              {delegateOpen && !selectedDelegate && (
                <div className="absolute z-20 mt-1 max-h-72 w-full overflow-y-auto rounded-xl border border-slate-200 bg-white py-1 shadow-lg">
                  <button
                    type="button"
                    onMouseDown={() => { set("delegateUserId", ""); setDelegateQuery(""); setDelegateOpen(false); }}
                    className="block w-full px-3 py-1.5 text-left text-sm text-slate-500 hover:bg-emerald-50"
                  >
                    None — don't delegate approvals
                  </button>
                  {filteredDelegates.length === 0 ? (
                    <p className="px-3 py-2 text-xs text-slate-400">
                      {delegateQuery.trim() ? "No matching employees" : "No other employees available"}
                    </p>
                  ) : (
                    filteredDelegates.map((e) => (
                      <button
                        key={e.userAccountId}
                        type="button"
                        onMouseDown={() => {
                          set("delegateUserId", e.userAccountId.toString());
                          setDelegateQuery("");
                          setDelegateOpen(false);
                        }}
                        className="block w-full px-3 py-1.5 text-left text-sm hover:bg-emerald-50"
                      >
                        <span className="font-medium text-slate-700">{e.fullName}</span>
                        {e.employeeNumber && <span className="ml-1.5 text-xs text-slate-400">({e.employeeNumber})</span>}
                        {(e.departmentName || e.jobTitle) && (
                          <span className="block text-xs text-slate-400">
                            {[e.departmentName, e.jobTitle].filter(Boolean).join(" — ")}
                          </span>
                        )}
                      </button>
                    ))
                  )}
                </div>
              )}
            </div>

            {form.delegateUserId && availableModules.length === 0 && (
              <p className="mt-3 text-xs font-semibold text-amber-700">
                This employee isn't currently set up as an approver for any module, so
                there's nothing to delegate -- clear the delegate above.
              </p>
            )}

            {form.delegateUserId && availableModules.length > 0 && (
              <div className="mt-3">
                <p className="mb-2 text-xs font-semibold text-slate-600">Modules to delegate</p>
                <div className="grid gap-2 sm:grid-cols-2">
                  {MODULE_ORDER.filter((module) => availableModules.includes(module)).map((module) => {
                    const checked = form.delegateModules.includes(module);
                    return (
                      <label
                        key={module}
                        className={`flex cursor-pointer items-center gap-2.5 rounded-xl border px-3 py-2.5 text-sm font-semibold transition-colors ${
                          checked
                            ? "border-emerald-300 bg-emerald-50 text-emerald-800"
                            : "border-slate-200 text-slate-700 hover:border-emerald-200 hover:bg-slate-50"
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => toggleDelegateModule(module)}
                          className="h-5 w-5 shrink-0 accent-emerald-600"
                        />
                        {MODULE_LABELS[module]}
                      </label>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        )}

        {submitError && (
          <p className="text-sm font-semibold text-red-600">{submitError}</p>
        )}

        <div className="flex gap-2">
          <Btn
            variant="primary"
            size="md"
            loading={saving}
            loadingText="Saving…"
          >
            {initial ? "Save Changes" : "Create Record"}
          </Btn>
          <Btn variant="secondary" size="md" onClick={onCancel}>
            Cancel
          </Btn>
        </div>
      </form>
    </div>
  );
}

// ─── helpers ─────────────────────────────────────────────────────────────────

function fmtDate(value?: string | null) {
  if (!value) return "—";
  try {
    return new Date(`${value}T00:00:00`).toLocaleDateString(undefined, {
      day: "numeric", month: "short", year: "numeric",
    });
  } catch { return value; }
}
